import { SortingState } from "@tanstack/react-table";
import { DashboardFilters } from "./types";
import { DashboardStatsResponse } from "@/shared/api";
import dayjs from "dayjs";
import { Option } from "react-multi-select-component";

export const ZERO_OPACITY = {
  zone_based_f1_score: 0,
  pin_based_f1_score: 0,
};

export const DEFAULT_OPACITY = {
  zone_based_f1_score: 1,
  pin_based_f1_score: 1,
};

const filterValueGetter =
  (searchParams: URLSearchParams, persistedJson: DashboardFilters | null) =>
  (key: string, defaultValue: string | Date | null = null, type?: "date") => {
    let result: any = defaultValue;

    const value = searchParams.get(key);

    if (value) {
      result = value;
    } else if (persistedJson && key in persistedJson) {
      result = persistedJson[key as keyof DashboardFilters];
    }

    if (dayjs(result).isValid() && type === "date") {
      result = new Date(result);
    }

    if (result && result.split !== undefined && result.split(",").length > 1) {
      result = result.split(",");
    }

    return result;
  };

const getDefaultFromDate = () =>
  dayjs().subtract(1, "week").startOf("day").utc().toDate();
const getDefaultToDate = () =>
  dayjs().add(1, "day").startOf("day").utc().toDate();

export const getDefaultFilters = (): DashboardFilters => ({
  from_datetime: getDefaultFromDate(),
  to_datetime: getDefaultToDate(),
  order_source: "all",
  stage_journey_annotation: "all",
  stage_transaction_annotation: "all",
  stage_track_persistence_annotation: "all",
  journeys_count: null,
  annotator_id: "all",
  order_id: null,
  sku_ids: [],
  journey_count_enabled: false,
});

export const getInitialFilters = (
  searchParams: URLSearchParams
): DashboardFilters => {
  const persisted = localStorage.getItem("dashboard-filters");
  const persistedJson = persisted
    ? (JSON.parse(persisted) as DashboardFilters)
    : null;

  const getFieldValue = filterValueGetter(searchParams, persistedJson);

  return {
    from_datetime: getFieldValue("from_datetime", getDefaultFromDate(), "date"),
    to_datetime: getFieldValue("to_datetime", getDefaultToDate(), "date"),
    order_source: getFieldValue("order_source", "all"),
    annotator_id: getFieldValue("annotator_id", "all"),
    stage_journey_annotation: getFieldValue("stage_journey_annotation", "all"),
    stage_transaction_annotation: getFieldValue(
      "stage_transaction_annotation",
      "all"
    ),
    stage_track_persistence_annotation: getFieldValue(
      "stage_track_persistence_annotation",
      "all"
    ),
    journeys_count: getFieldValue("journeys_count"),
    sku_ids: [],
    order_id: getFieldValue("order_id"),
    journey_count_enabled:
      getFieldValue("journey_count_enabled") === "true" ||
      getFieldValue("journey_count_enabled") === true,
  };
};

export const convertDateToUTC = (date: string | Date | null) =>
  date ? dayjs(date).utc(true).toISOString() : undefined;

export const parseFilterValue = (value: string | null) =>
  value === "all" || !value ? undefined : value;

export const parseSkuIds = (value: Option[]) => {
  if (!value.length) return undefined;
  return value.map(({ value }: Option) => value).join(",");
};

export const parseSorting = (sorting: SortingState) => {
  return sorting.length > 0
    ? sorting.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(",")
    : undefined;
};

/**
 * Splits dashboard stats data into separate arrays for zone-based and pin-based metrics
 */
export function splitStatsData(data: DashboardStatsResponse | undefined) {
  if (!data?.results) {
    return {
      zoneBasedData: { results: [], count: 0 },
      pinBasedData: { results: [], count: 0 },
    };
  }

  const zoneBasedResults = data.results.map((item) => ({
    ...item,
    f1_score: item.zone_based_f1_score,
    // Keep original field for backward compatibility
    zone_based_f1_score: item.zone_based_f1_score,
    // Remove pin-based field to avoid confusion
    pin_based_f1_score: undefined,
  }));

  const pinBasedResults = data.results.map((item) => ({
    ...item,
    f1_score: item.pin_based_f1_score,
    // Keep original field for backward compatibility
    pin_based_f1_score: item.pin_based_f1_score,
    // Remove zone-based field to avoid confusion
    zone_based_f1_score: undefined,
  }));

  return {
    zoneBasedData: {
      results: zoneBasedResults,
      count: data.count,
    },
    pinBasedData: {
      results: pinBasedResults,
      count: data.count,
    },
  };
}

/**
 * Merges two separate stats datasets for comparison, adding version suffixes
 */
export function mergeStatsForComparison(
  dataA: DashboardStatsResponse | undefined,
  dataB: DashboardStatsResponse | undefined,
  metricType: "zone_based" | "pin_based"
) {
  if (!dataA?.results && !dataB?.results) {
    return { results: [], count: 0 };
  }

  const resultsA = dataA?.results || [];
  const resultsB = dataB?.results || [];
  const scoreField =
    metricType === "zone_based" ? "zone_based_f1_score" : "pin_based_f1_score";

  // Create a map to merge data by order_id and requested_at
  const mergedMap = new Map<string, any>();

  // Add data from first dataset (A)
  resultsA.forEach((item) => {
    const key = item.order_id;

    const newData = {
      ...item,
      order_id: item.order_id,
      [`${scoreField}_A`]: item[scoreField],
      pipeline_version_A: item.pipeline_version,
      requested_at_A: item.requested_at,
    };

    mergedMap.set(key, newData);
  });

  // Add data from second dataset (B)
  resultsB.forEach((item) => {
    const key = item.order_id;

    if (mergedMap.has(key)) {
      // Merge with existing data
      const existing = mergedMap.get(key);

      const newData = {
        ...existing,
        [`${scoreField}_B`]: item[scoreField],
        pipeline_version_B: item.pipeline_version,
        requested_at_B: item.requested_at,
        isMatched:
          item[scoreField] !== null && existing[`${scoreField}_A`] !== null,
      };
      mergedMap.set(key, newData);
    } else {
      // Create new entry

      const newData = {
        ...item,
        order_id: item.order_id,
        [`${scoreField}_B`]: item[scoreField],
        pipeline_version_B: item.pipeline_version,
        requested_at_B: item.requested_at,
      };

      mergedMap.set(key, newData);
    }
  });

  const mergedResults = Array.from(mergedMap.values()).sort(
    (a, b) =>
      new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime()
  );

  return {
    results: mergedResults,
    count: mergedResults.length,
  };
}
